import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import convertToCurrency from '@naturehouse/nh-essentials/lib/currency/currencyHelper';
import {
    FORMATS,
    intlDateFormat
} from '@naturehouse/nh-essentials/lib/dates/intlDateTimeFormatter';
import HttpClient from '../../../common/HttpClient';
import type { HousePrice } from './HousePrices';
import HousePrices from './HousePrices';
import HouseSearchFilters from './HouseSearchFilters';
import HouseSearchListItems from './HouseSearchListItems';

export default class HousePricingApi implements Observer {
    static #instance: HousePricingApi | null = null;

    readonly #atleisurePricesApiURL = `${window.location.origin}/price/houses/at-leisure`;

    readonly #listItems = HouseSearchListItems.getInstance();

    readonly #housePrices = HousePrices.getInstance();

    readonly #filters = HouseSearchFilters.getInstance();

    readonly #httpClient: HttpClient = HttpClient;

    public static getInstance(): HousePricingApi {
        if (!HousePricingApi.#instance) {
            HousePricingApi.#instance = new HousePricingApi(
                HouseSearchListItems.getInstance(),
                HousePrices.getInstance()
            );
        }

        return HousePricingApi.#instance;
    }

    constructor(listItems: HouseSearchListItems, prices: HousePrices) {
        this.#listItems = listItems;
        this.#housePrices = prices;

        listItems.attach(this);
        this.update();
    }

    update(): void {
        const externalPricingHouses = this.#listItems.houses.filter((house) => house.externalId);

        if (externalPricingHouses.length === 0) {
            return;
        }

        this.#loadPricing(externalPricingHouses);
    }

    async #loadPricing(houses: HouseSearchListItem[]): Promise<void> {
        const houseCodes: string[] = houses.map((house) => house.externalId) as string[];
        const params = this.#filters.activeParams;
        params.houseCodes = houseCodes.join(',');
        const queryParams = new URLSearchParams(params).toString();

        const url = `${this.#atleisurePricesApiURL}?${queryParams}`;
        const results = await this.#httpClient.get(url);
        const houseStayPrices = results as HouseStayPriceResponse[];

        const prices: HousePrice[] = [];

        if (!Array.isArray(houseStayPrices)) {
            return;
        }

        for (const house of houses.filter((house) => house.externalId)) {
            const pricing = houseStayPrices.find((p) => p.houseCode === house.externalId);

            prices.push({
                id: house.id,
                price:
                    pricing === undefined
                        ? null
                        : {
                              total: (pricing.price / 100).toString(),
                              totalFormatted: convertToCurrency(pricing.price / 100).normalize(
                                  'NFKC'
                              ),
                              totalWithDiscount: '',
                              dayPrice: (pricing.pricePerDay / 100).toString(),
                              dayPriceFormatted: convertToCurrency(
                                  pricing.pricePerDay / 100
                              ).normalize('NFKC'),
                              dayPriceWithDiscount: '',
                              startDate: pricing.arrivalDate,
                              endDate: pricing.departureDate,
                              startDateFormatted: intlDateFormat(pricing.arrivalDate, FORMATS.LONG),
                              stayDescription: pricing.stayType
                          }
            });
        }

        this.#housePrices.prices = prices;
    }
}
